class LymphivaWellnessQuiz extends HTMLElement {
  connectedCallback() {
    this.config = this.readConfig();
    this.intro = this.querySelector('[data-ly-quiz-intro]');
    this.form = this.querySelector('[data-ly-quiz-form]');
    this.result = this.querySelector('[data-ly-quiz-result]');
    this.steps = [...this.querySelectorAll('[data-ly-quiz-step]')];
    this.progressLabel = this.querySelector('[data-ly-quiz-progress-label]');
    this.progress = this.querySelector('[role="progressbar"]');
    this.progressBar = this.querySelector('[data-ly-quiz-progress-bar]');
    this.live = this.querySelector('[data-ly-quiz-live]');
    this.nextButton = this.querySelector('[data-ly-quiz-next]');
    this.backButton = this.querySelector('[data-ly-quiz-back]');
    this.answers = new Map();
    this.currentStep = -1;

    this.querySelector('[data-ly-quiz-start]')?.addEventListener('click', () => this.start());
    this.nextButton?.addEventListener('click', () => this.next());
    this.backButton?.addEventListener('click', () => this.back());
    this.form?.addEventListener('change', (event) => this.capture(event));
    this.form?.addEventListener('submit', (event) => event.preventDefault());
    window.addEventListener('popstate', (event) => this.onPopState(event));
    history.replaceState({ ...history.state, lymphivaQuizStep: 'intro' }, '', location.href);
    this.publish('lymphiva_quiz_view');
  }

  readConfig() {
    try {
      return JSON.parse(this.querySelector('[data-ly-quiz-config]')?.textContent || '{}');
    } catch (_error) {
      return {};
    }
  }

  publish(name, detail = {}) {
    const payload = { ...detail };
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
    const privacy = window.Shopify?.customerPrivacy;
    const allowed = !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
    if (!allowed) return;
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
    if (typeof window.Shopify?.analytics?.publish === 'function') {
      const publication = window.Shopify.analytics.publish(name, payload);
      publication?.catch?.(() => {});
    }
  }

  start() {
    this.intro.hidden = true;
    this.form.hidden = false;
    this.showStep(0, true);
    this.publish('lymphiva_quiz_start');
  }

  capture(event) {
    const input = event.target.closest('input[type="radio"]');
    if (!input) return;
    this.answers.set(input.name, {
      value: input.value,
      points: input.hasAttribute('data-points') ? Number(input.dataset.points) : null
    });
    this.nextButton.disabled = false;
  }

  next() {
    const step = this.steps[this.currentStep];
    const checked = step?.querySelector('input[type="radio"]:checked');
    if (!checked) {
      this.nextButton.disabled = true;
      step?.querySelector('input[type="radio"]')?.focus();
      return;
    }
    this.publish('lymphiva_quiz_step_complete', { step: this.currentStep + 1 });
    if (this.currentStep === this.steps.length - 1) {
      this.complete();
      return;
    }
    this.showStep(this.currentStep + 1, true);
  }

  back() {
    if (history.state?.lymphivaQuizStep !== undefined) history.back();
    else this.showStep(Math.max(0, this.currentStep - 1), false);
  }

  onPopState(event) {
    const destination = event.state?.lymphivaQuizStep;
    if (destination === 'intro' || destination === undefined) {
      this.showIntro();
      return;
    }
    if (destination === 'result') {
      if (this.answers.size === 7) this.renderResult(false);
      else this.showIntro();
      return;
    }
    const step = Number(destination);
    if (Number.isInteger(step) && step >= 0 && step < this.steps.length) this.showStep(step, false);
  }

  showIntro() {
    this.currentStep = -1;
    this.intro.hidden = false;
    this.form.hidden = true;
    this.result.hidden = true;
    this.querySelector('[data-ly-quiz-start]')?.focus();
  }

  showStep(index, pushHistory) {
    this.currentStep = index;
    this.intro.hidden = true;
    this.result.hidden = true;
    this.form.hidden = false;
    this.steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== index; });
    const number = index + 1;
    this.progressLabel.textContent = `Question ${number} of ${this.steps.length}`;
    this.progress.setAttribute('aria-valuenow', String(number));
    this.progressBar.style.width = `${(number / this.steps.length) * 100}%`;
    this.live.textContent = `Question ${number} of ${this.steps.length}`;
    this.nextButton.textContent = number === this.steps.length ? 'See My Results' : 'Continue';
    this.nextButton.disabled = !this.steps[index].querySelector('input[type="radio"]:checked');
    this.backButton.textContent = index === 0 ? 'Back' : 'Back';
    if (pushHistory) history.pushState({ ...history.state, lymphivaQuizStep: index }, '', `#quiz-step-${number}`);
    this.steps[index].setAttribute('tabindex', '-1');
    requestAnimationFrame(() => this.steps[index].focus({ preventScroll: false }));
  }

  complete() {
    history.pushState({ ...history.state, lymphivaQuizStep: 'result' }, '', '#quiz-result');
    this.renderResult(true);
  }

  renderResult(emitEvent) {
    const scored = ['quiz_comfort', 'quiz_hydration', 'quiz_movement', 'quiz_consistency'];
    const total = scored.reduce((sum, key) => sum + (this.answers.get(key)?.points ?? 0), 0);
    const score = Math.round((total / 12) * 100);
    const band = score >= 75
      ? ['Strong Routine Foundation', 'Your answers suggest several consistent everyday habits are already in place.']
      : score >= 50
        ? ['A Good Foundation to Build On', 'You have a useful base, with a few places where small routines could become more consistent.']
        : score >= 25
          ? ['A Few Routine Gaps', 'A few simple, repeatable anchors may help make your daily wellness routine easier to maintain.']
          : ['Start With the Basics', 'Begin with small, practical habits you can repeat without overcomplicating your day.'];

    this.form.hidden = true;
    this.intro.hidden = true;
    this.result.hidden = false;
    this.querySelector('[data-ly-quiz-score]').textContent = String(score);
    const ring = this.querySelector('[data-ly-quiz-ring]');
    ring.style.setProperty('--score', '0');
    requestAnimationFrame(() => ring.style.setProperty('--score', String(score)));
    ring.setAttribute('aria-label', `Lymphatic Wellness Score: ${score} out of 100, ${band[0]}`);
    this.querySelector('[data-ly-quiz-band]').textContent = band[0];
    this.querySelector('[data-ly-quiz-explanation]').textContent = this.goalCopy(this.answers.get('quiz_goal')?.value, band[1]);
    this.renderCategories(scored);
    this.querySelector('[data-ly-quiz-schedule]').textContent = this.scheduleCopy(this.answers.get('quiz_anchor')?.value);
    this.renderOffer();
    this.result.focus({ preventScroll: false });
    if (emitEvent) this.publish('lymphiva_quiz_complete');
  }

  goalCopy(goal, fallback) {
    const copies = {
      comfort: 'A lighter-feeling routine starts with consistent daily anchors. ' + fallback,
      consistency: 'Hydration and movement are the clearest places to build repeatable anchors. ' + fallback,
      botanical: 'A botanical habit works best when it is attached to moments already in your day. ' + fallback,
      fit: 'Your result focuses on whether a simple liquid ritual fits the habits you already have. ' + fallback
    };
    return copies[goal] || fallback;
  }

  scheduleCopy(anchor) {
    return ({
      meals: 'Pair your servings with breakfast and dinner',
      water_winddown: 'Pair your servings with morning water and your evening wind-down',
      work_reset: 'Pair your servings with the start of work and an afternoon reset',
      flexible: 'Choose two visible, repeatable moments each day'
    })[anchor] || 'Choose two repeatable moments each day';
  }

  renderCategories(keys) {
    const names = ['Everyday comfort', 'Hydration consistency', 'Movement breaks', 'Routine consistency'];
    const statuses = ['Start with one small step', 'Opportunity to build', 'Mostly consistent', 'Strong foundation'];
    const container = this.querySelector('[data-ly-quiz-categories]');
    container.replaceChildren(...keys.map((key, index) => {
      const row = document.createElement('div');
      row.className = 'ly-quiz__category';
      const label = document.createElement('span');
      const value = document.createElement('strong');
      label.textContent = names[index];
      value.textContent = statuses[this.answers.get(key)?.points ?? 0];
      row.append(label, value);
      return row;
    }));
  }

  renderOffer() {
    const offerKey = this.answers.get('quiz_offer')?.value || 'one';
    const offer = this.config.offers?.[offerKey] || this.config.offers?.one;
    if (!offer) return;
    this.querySelector('[data-ly-quiz-offer-title]').textContent = offer.title;
    this.querySelector('[data-ly-quiz-offer-detail]').textContent = offer.detail;
    const url = new URL(this.config.productUrl, location.origin);
    url.searchParams.set('quiz_bundle', offer.bundle);
    url.searchParams.set('quiz_purchase', offer.purchase);
    url.searchParams.set('quiz_source', 'wellness');
    url.hash = 'lymphiva-purchase';
    this.querySelector('[data-ly-quiz-offer-link]').href = url.toString();
    this.querySelector('[data-ly-quiz-compare]').href = `${this.config.productUrl}#lymphiva-purchase`;
    this.querySelector('[data-ly-quiz-offer-link]').onclick = () => {
      this.publish('lymphiva_quiz_offer_selected', { bundle: offer.bundle, purchase_type: offer.purchase });
      this.publish('lymphiva_quiz_pdp_click');
    };
  }
}

if (!customElements.get('lymphiva-wellness-quiz')) {
  customElements.define('lymphiva-wellness-quiz', LymphivaWellnessQuiz);
}
